import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "@shared/client/components/toast/show-toast";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faPeoplePantsSimple, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { collectionStore } from "@/data/collection-store";
import { WipBorder } from "@/components/wip-border/wip-border";
import "./add-to-group-popover.scss";
const MAX_GROUP_TITLE_LENGTH = 200;
/**
 * Picker for "Add to group" — moves one-or-more layer-1 Collections
 * under a single layer-0 Group. Single-select: a Collection has
 * exactly one parent at a time, so we don't fan out across many
 * destinations the way `AddToCollectionPopover` does for blocks.
 *
 * Includes inline "New group" creation: typing a name + Create
 * provisions a fresh layer-0 collection and pre-selects it as the
 * destination, so the user can create-and-move in one motion.
 */
export const AddToGroupPopover = memo(({ collectionIds, onClose, onMoved, }) => {
    const ref = useRef(null);
    const { groups, isLoading } = collectionStore.useListGroups();
    const [selectedGroupId, setSelectedGroupId] = useState(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [isCreatePending, setIsCreatePending] = useState(false);
    const visibleGroups = useMemo(() => groups, [groups]);
    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);
    const handleCreateSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = newTitle.trim();
        if (trimmed.length === 0)
            return;
        setIsCreatePending(true);
        try {
            const id = await collectionStore.createGroup({ title: trimmed });
            setSelectedGroupId(id);
            setNewTitle("");
            setIsCreating(false);
        }
        catch {
            showToast.error("Failed to create group");
        }
        finally {
            setIsCreatePending(false);
        }
    }, [newTitle]);
    const handleMove = useCallback(async () => {
        if (!selectedGroupId)
            return;
        if (collectionIds.length === 0)
            return;
        setIsSubmitting(true);
        try {
            const result = await collectionStore.moveCollectionsToGroup(collectionIds, selectedGroupId);
            const total = collectionIds.length;
            const collectionPhrase = total === 1 ? "collection" : `${total} collections`;
            // Build the user-visible message. If the server returned warnings
            // (e.g. "you were removed as direct owner; access is now inherited
            // from <group>" or "<N> other members lost direct access"),
            // surface them so the user understands what changed. Otherwise
            // show the plain success message.
            const warningSuffix = result.warnings.length > 0 ? `\n\n${result.warnings.join("\n")}` : "";
            if (result.failures.length === 0) {
                showToast.success(`Moved ${collectionPhrase} to group${warningSuffix}`);
                onMoved?.();
            }
            else if (result.movedCount === 0) {
                showToast.error("Failed to move collections");
            }
            else {
                showToast.warning(`Moved ${result.movedCount} of ${total} ${total === 1 ? "collection" : "collections"}${warningSuffix}`);
                onMoved?.();
            }
        }
        finally {
            setIsSubmitting(false);
            onClose();
        }
    }, [collectionIds, selectedGroupId, onClose, onMoved]);
    const moveLabel = (() => {
        if (collectionIds.length === 0)
            return "Move";
        if (collectionIds.length === 1)
            return "Move";
        return `Move ${collectionIds.length}`;
    })();
    return (_jsxs("div", { className: "add-to-group-popover", ref: ref, role: "dialog", "aria-label": "Add to group", children: [_jsx(WipBorder, {}), _jsx("div", { className: "add-to-group-popover__header", children: "Add to group" }), _jsx("div", { className: "add-to-group-popover__list", children: isLoading && visibleGroups.length === 0 ? (_jsx("div", { className: "add-to-group-popover__empty", children: "Loading\u2026" })) : visibleGroups.length === 0 ? (_jsx("div", { className: "add-to-group-popover__empty", children: "No groups yet \u2014 create one below" })) : (visibleGroups.map((group) => {
                    const checked = selectedGroupId === group.id;
                    const title = group.title || "Untitled group";
                    return (_jsxs("button", { type: "button", role: "radio", "aria-checked": checked, className: [
                            "add-to-group-popover__item",
                            checked && "add-to-group-popover__item--checked",
                        ]
                            .filter(Boolean)
                            .join(" "), onClick: () => setSelectedGroupId(group.id), children: [_jsx("span", { className: "add-to-group-popover__radio", "aria-hidden": true, children: _jsx("span", { className: "add-to-group-popover__radio-dot" }) }), group.metadata.emoji ? (_jsx("span", { className: "add-to-group-popover__item-emoji", "aria-hidden": true, children: group.metadata.emoji })) : (_jsx(FaIcon, { icon: faPeoplePantsSimple, className: "add-to-group-popover__item-icon" })), _jsx("span", { className: "add-to-group-popover__item-label", children: title })] }, group.id));
                })) }), isCreating ? (_jsxs("form", { className: "add-to-group-popover__create", onSubmit: handleCreateSubmit, children: [_jsx("input", { type: "text", className: "add-to-group-popover__create-input", placeholder: "New group name\u2026", value: newTitle, onChange: (e) => setNewTitle(e.target.value), maxLength: MAX_GROUP_TITLE_LENGTH, autoFocus: true, disabled: isCreatePending }), _jsx("button", { type: "button", className: "add-to-group-popover__create-cancel", onClick: () => {
                            setIsCreating(false);
                            setNewTitle("");
                        }, disabled: isCreatePending, children: "Cancel" }), _jsx("button", { type: "submit", className: "add-to-group-popover__create-submit", disabled: newTitle.trim().length === 0 || isCreatePending, children: isCreatePending ? "…" : "Create" })] })) : (_jsxs("button", { type: "button", className: "add-to-group-popover__new", onClick: () => setIsCreating(true), children: [_jsx(FaIcon, { icon: faPlus, className: "add-to-group-popover__new-icon" }), _jsx("span", { children: "New group" })] })), _jsxs("div", { className: "add-to-group-popover__footer", children: [_jsx("button", { type: "button", className: "add-to-group-popover__cancel", onClick: onClose, disabled: isSubmitting, children: "Cancel" }), _jsx("button", { type: "button", className: "add-to-group-popover__submit", onClick: handleMove, disabled: !selectedGroupId || isSubmitting, children: isSubmitting ? "Moving…" : moveLabel })] })] }));
});
